import React, { useEffect, useState } from "react";
import { Receipt, FileSignature, AlertCircle, DollarSign, Sparkles, FileDown, Activity } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Page, Card, Pill, StatCard, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";

const TABS = [
  { key: "dashboard", label: "Revenue" },
  { key: "eligibility", label: "Eligibility" },
  { key: "claims", label: "Claims" },
  { key: "denials", label: "Denials" },
  { key: "payments", label: "Payments" },
  { key: "statements", label: "Statements" },
];

const PAYER_OPTIONS = ["Aetna", "BCBS", "Cigna", "United Healthcare", "Humana", "Medicaid", "Medicare", "Tricare"];

export default function Insurance() {
  const [tab, setTab] = useState("dashboard");
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [claims, setClaims] = useState([]);
  const [eligibility, setEligibility] = useState({});
  const [payments, setPayments] = useState([]);
  const [statements, setStatements] = useState([]);
  const [denials, setDenials] = useState(null);
  const [coding, setCoding] = useState(null);
  const [activeAppeal, setActiveAppeal] = useState(null);

  // Eligibility form
  const [elig, setElig] = useState({ client_id: "", payer: "Aetna", member_id: "", plan_type: "commercial" });
  // New claim form
  const [claimForm, setClaimForm] = useState({ client_id: "", cpt_code: "90837", icd10_codes: "F33.1", modifiers: "", amount: 150, date_of_service: new Date().toISOString().slice(0,10), payer: "Aetna", member_id: "" });
  // AI coding suggest
  const [suggest, setSuggest] = useState({ client_id: "", session_type: "individual", duration_minutes: 53, diagnosis: "" });

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/insurance/revenue/dashboard").then(({ data }) => setDashboard(data)),
    api.get("/insurance/claims").then(({ data }) => setClaims(data)),
    api.get("/insurance/payments").then(({ data }) => setPayments(data)),
    api.get("/insurance/statements").then(({ data }) => setStatements(data)),
    api.get("/insurance/denials/analytics").then(({ data }) => setDenials(data)),
  ]);
  useEffect(() => { load(); }, []);

  const checkEligibility = async () => {
    if (!elig.client_id || !elig.member_id) return toast.error("Client and member ID required");
    try {
      const { data } = await api.post("/insurance/eligibility", elig);
      setEligibility({ ...eligibility, [elig.client_id]: data });
      toast.success(`Eligibility: ${data.status}`);
    } catch { toast.error("Check failed"); }
  };

  const submitClaim = async () => {
    if (!claimForm.client_id) return toast.error("Select a client");
    try {
      await api.post("/insurance/claims", {
        ...claimForm,
        icd10_codes: claimForm.icd10_codes.split(",").map(s => s.trim()).filter(Boolean),
        modifiers: claimForm.modifiers.split(",").map(s => s.trim()).filter(Boolean),
        amount: parseFloat(claimForm.amount),
      });
      toast.success("Claim submitted (mock — real clearinghouse pending)");
      load();
    } catch { toast.error("Submission failed"); }
  };

  const suggestCoding = async () => {
    if (!suggest.client_id) return toast.error("Select a client");
    try {
      const { data } = await api.post("/insurance/coding/suggest", { ...suggest, duration_minutes: parseInt(suggest.duration_minutes) });
      setCoding(data);
      // Pre-fill claim
      setClaimForm({ ...claimForm, client_id: suggest.client_id, cpt_code: data.cpt_code, icd10_codes: data.icd10_code, modifiers: data.modifiers.join(",") });
      toast.success("Codes suggested");
    } catch { toast.error("Failed"); }
  };

  const denyClaim = async (id) => {
    const code = window.prompt("Enter CARC denial code (e.g. 197):");
    if (!code) return;
    try {
      await api.put(`/insurance/claims/${id}`, { status: "Denied", denial_code: code, denial_reason: "Auto" });
      toast.success("Claim marked denied");
      load();
    } catch { toast.error("Failed"); }
  };

  const generateAppeal = async (id) => {
    try {
      const { data } = await api.post(`/insurance/claims/${id}/appeal`);
      setActiveAppeal(data);
      load();
    } catch { toast.error("Failed"); }
  };

  const generateSuperbill = async (id) => {
    try {
      const { data } = await api.post(`/insurance/superbill/${id}`);
      const blob = new Blob([Uint8Array.from(atob(data.pdf_base64), c => c.charCodeAt(0))], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("Superbill generated");
    } catch { toast.error("Failed"); }
  };

  const set = (form, setter) => (k) => (e) => setter({ ...form, [k]: e.target.value });

  const COLORS = ["#1A6B72", "#7BAE7F", "#C9A84C", "#B45253", "#5C6B7A"];

  return (
    <Page title="Insurance Billing" subtitle="Eligibility · Claims · Denials · Payments · Superbills · Revenue">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className="px-4 py-2 rounded-full text-sm font-medium transition-all" style={tab === t.key ? { background: "var(--mf-primary)", color: "#FAFAF8" } : { color: "var(--mf-text-muted)" }} data-testid={`ins-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && dashboard && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Billed" value={`$${dashboard.total_billed}`} icon={DollarSign} accent="var(--mf-primary)" delay={0.05} testid="ins-billed" />
            <StatCard label="Collected" value={`$${dashboard.total_collected}`} icon={DollarSign} accent="var(--mf-secondary)" delay={0.1} testid="ins-collected" />
            <StatCard label="Collection Rate" value={`${Math.round(dashboard.collection_rate * 100)}%`} icon={Activity} accent="var(--mf-accent)" delay={0.15} testid="ins-collection-rate" />
            <StatCard label="Denial Rate" value={`${Math.round(dashboard.denial_rate * 100)}%`} icon={AlertCircle} accent="#B45253" delay={0.2} testid="ins-denial-rate" />
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <Card delay={0.25} testid="ins-aging-chart">
              <h3 className="font-serif text-xl mb-4">Outstanding Claims Aging</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard.aging}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="bucket" stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                    <YAxis stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                    <Tooltip contentStyle={{ background: "var(--mf-surface)", border: "none", borderRadius: "12px" }} />
                    <Bar dataKey="amount" fill="var(--mf-primary)" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card delay={0.3} testid="ins-payer-pie">
              <h3 className="font-serif text-xl mb-4">Revenue by Payer</h3>
              <div className="h-64">
                {dashboard.by_payer.length === 0 ? <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--mf-text-muted)" }}>No payer data yet</div> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dashboard.by_payer} dataKey="collected" nameKey="payer" cx="50%" cy="50%" outerRadius={80} label>
                        {dashboard.by_payer.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--mf-surface)", border: "none", borderRadius: "12px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "eligibility" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card testid="elig-form" delay={0.05}>
            <h2 className="font-serif text-2xl mb-4">Real-Time Eligibility</h2>
            <div className="space-y-3">
              <select value={elig.client_id} onChange={set(elig, setElig)("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="elig-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <select value={elig.payer} onChange={set(elig, setElig)("payer")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="elig-payer">
                {PAYER_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
              <input placeholder="Member ID" value={elig.member_id} onChange={set(elig, setElig)("member_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="elig-member-id" />
              <PrimaryButton onClick={checkEligibility} testid="elig-check-btn">Check Eligibility</PrimaryButton>
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>* Mock check — real Change Healthcare/Waystar pending API keys</div>
            </div>
          </Card>
          <Card testid="elig-result" delay={0.1}>
            <h2 className="font-serif text-2xl mb-4">Result</h2>
            {Object.keys(eligibility).length === 0 ? <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Run an eligibility check to see results.</div> : (
              <div className="space-y-3">
                {Object.entries(eligibility).map(([cid, e]) => (
                  <div key={cid} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{e.payer}</div>
                      <Pill variant={e.status === "green" ? "success" : e.status === "yellow" ? "warn" : "danger"}>
                        {e.status === "green" ? "Active · In-Network" : e.status === "yellow" ? "Out of Network" : "Inactive"}
                      </Pill>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-xs uppercase" style={{ color: "var(--mf-text-muted)" }}>Deductible</span><div>${e.deductible_met} / ${e.deductible}</div></div>
                      <div><span className="text-xs uppercase" style={{ color: "var(--mf-text-muted)" }}>Copay</span><div>${e.copay}</div></div>
                      <div><span className="text-xs uppercase" style={{ color: "var(--mf-text-muted)" }}>Sessions</span><div>{e.sessions_remaining}/{e.session_limit}</div></div>
                      <div><span className="text-xs uppercase" style={{ color: "var(--mf-text-muted)" }}>Parity</span><div>{e.mental_health_parity ? "✓" : "✗"}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "claims" && (
        <div className="space-y-5">
          <Card testid="ai-coding-card" delay={0.05}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5" style={{ color: "var(--mf-accent)" }} />
              <h2 className="font-serif text-2xl">AI Session Coding</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <select value={suggest.client_id} onChange={set(suggest, setSuggest)("client_id")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="coding-client">
                <option value="">Client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <select value={suggest.session_type} onChange={set(suggest, setSuggest)("session_type")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="coding-type">
                {["individual","family","intake","crisis","medication","telehealth","group"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" placeholder="Duration (min)" value={suggest.duration_minutes} onChange={set(suggest, setSuggest)("duration_minutes")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="coding-duration" />
              <PrimaryButton onClick={suggestCoding} testid="coding-suggest-btn">Suggest Codes</PrimaryButton>
            </div>
            {coding && (
              <div className="mt-4 p-4 rounded-xl" style={{ background: "rgba(201,168,76,0.1)" }}>
                <div className="flex flex-wrap gap-2 mb-2">
                  <Pill variant="primary">CPT {coding.cpt_code}</Pill>
                  <Pill>{coding.icd10_code}</Pill>
                  {coding.modifiers.map(m => <Pill key={m} variant="warn">{m}</Pill>)}
                </div>
                <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>{coding.ai_validation}</div>
              </div>
            )}
          </Card>

          <Card testid="claim-submit-card" delay={0.1}>
            <h2 className="font-serif text-2xl mb-4">Submit Claim</h2>
            <div className="grid md:grid-cols-3 gap-3">
              <select value={claimForm.client_id} onChange={set(claimForm, setClaimForm)("client_id")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-client">
                <option value="">Client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <input placeholder="CPT" value={claimForm.cpt_code} onChange={set(claimForm, setClaimForm)("cpt_code")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-cpt" />
              <input placeholder="ICD-10 (csv)" value={claimForm.icd10_codes} onChange={set(claimForm, setClaimForm)("icd10_codes")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-icd" />
              <input placeholder="Modifiers" value={claimForm.modifiers} onChange={set(claimForm, setClaimForm)("modifiers")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-mod" />
              <input type="number" placeholder="Amount" value={claimForm.amount} onChange={set(claimForm, setClaimForm)("amount")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-amount" />
              <DatePicker value={claimForm.date_of_service} onChange={set(claimForm, setClaimForm)("date_of_service")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-date" />
              <select value={claimForm.payer} onChange={set(claimForm, setClaimForm)("payer")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-payer">
                {PAYER_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
              <input placeholder="Member ID (optional)" value={claimForm.member_id} onChange={set(claimForm, setClaimForm)("member_id")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="claim-member" />
              <PrimaryButton onClick={submitClaim} testid="claim-submit-btn">Submit Claim</PrimaryButton>
            </div>
          </Card>

          <Card testid="claims-list" delay={0.15}>
            <h3 className="font-serif text-xl mb-4">All Claims</h3>
            {claims.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>No claims yet.</div> : (
              <div className="space-y-2">
                {claims.map(c => (
                  <div key={c.id} className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid={`claim-${c.id}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs">{c.claim_number}</span>
                        <Pill variant="primary">CPT {c.cpt_code}</Pill>
                        <Pill>{c.payer}</Pill>
                        <Pill variant={c.status === "Paid" ? "success" : c.status === "Denied" ? "danger" : "warn"}>{c.status}</Pill>
                      </div>
                      <div className="font-serif text-lg" style={{ color: "var(--mf-primary)" }}>${c.amount}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => generateSuperbill(c.id)} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(26,107,114,0.12)", color: "var(--mf-primary)" }} data-testid={`super-${c.id}`}><FileDown className="w-3 h-3 inline" /> Superbill</button>
                      {c.status === "Submitted" && <button onClick={() => denyClaim(c.id)} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(180,82,83,0.1)", color: "#b91c1c" }} data-testid={`deny-${c.id}`}>Mark Denied</button>}
                      {c.status === "Denied" && !c.appeal_letter && <button onClick={() => generateAppeal(c.id)} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.18)", color: "#8a6f20" }} data-testid={`appeal-${c.id}`}><Sparkles className="w-3 h-3 inline" /> Generate Appeal</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          {activeAppeal && (
            <Card testid="appeal-display">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-xl">Appeal Letter</h3>
                <button onClick={() => setActiveAppeal(null)} className="text-sm" style={{ color: "var(--mf-text-muted)" }}>Close</button>
              </div>
              <div className="p-4 rounded-xl text-sm whitespace-pre-wrap mf-prose" style={{ background: "var(--mf-muted)" }}>{activeAppeal.appeal_letter}</div>
            </Card>
          )}
        </div>
      )}

      {tab === "denials" && denials && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Total Denials" value={denials.total_denials} icon={AlertCircle} accent="#B45253" delay={0.05} testid="den-total" />
            <StatCard label="Revenue at Risk" value={`$${denials.revenue_at_risk}`} icon={DollarSign} accent="var(--mf-accent)" delay={0.1} testid="den-revenue" />
            <StatCard label="Appeal Recovered" value={`$${dashboard?.appeal_recovered || 0}`} icon={Sparkles} accent="var(--mf-secondary)" delay={0.15} testid="den-recovered" />
          </div>
          <Card testid="den-by-code" delay={0.2}>
            <h3 className="font-serif text-xl mb-4">Top Denial Codes (CARC)</h3>
            {denials.by_code.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>No denials yet.</div> : (
              <div className="space-y-2">
                {denials.by_code.map(d => (
                  <div key={d.code} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }}>
                    <div>
                      <div className="font-medium">CARC {d.code} — {d.explanation}</div>
                    </div>
                    <Pill variant="danger">{d.count}</Pill>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "payments" && (
        <Card testid="payments-list">
          <h3 className="font-serif text-xl mb-4">Payments Posted</h3>
          {payments.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>No payments posted yet.</div> : (
            <div className="space-y-2">
              {payments.map(p => (
                <div key={p.id} className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">${p.insurance_payment} insurance + ${p.patient_responsibility} patient</div>
                      <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{p.payment_date}</div>
                    </div>
                    {p.underpayment_flagged && <Pill variant="warn">Underpaid ${Math.abs(p.underpayment)}</Pill>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "statements" && (
        <Card testid="statements-list">
          <h3 className="font-serif text-xl mb-4">Client Statements</h3>
          {statements.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>No statements sent yet.</div> : (
            <div className="space-y-2">
              {statements.map(s => (
                <div key={s.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="text-sm font-medium">${s.amount}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Due {new Date(s.due_date).toLocaleDateString()} · {s.description}</div>
                  </div>
                  <Pill variant={s.status === "paid" ? "success" : "warn"}>{s.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </Page>
  );
}
